import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import AddIcon from '@mui/icons-material/AddSharp';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import ScheduleSend from '@mui/icons-material/ScheduleSendOutlined';
import Send from '@mui/icons-material/SendOutlined';
import { CampaignMedium } from '@prisma/client';
import { toast } from 'react-toastify';
import { isPast } from 'date-fns';
import { Typography } from '@mui/material';
import {
  CampaignSegment,
  CreateUpdateCampaignPayload,
  EmailContentSchema,
  getUpsertCampaignSchema,
  MarketingCampaignSegmentWithFilters,
  MarketingCampaignWithSource,
  SmsContentSchema,
} from '../../../types/marketing';
import StepsFlow, { StepFormData, StepInput } from '../../global/StepsFlow';
import FullPageModal from '../../modal/FullPageModal';
import {
  getPrebuiltSegments,
  PREBUILT_EXCLUDE_SEGMENTS_CONTENT,
  PREBUILT_INCLUDE_SEGMENTS_CONTENT,
  TEMPLATE_PLACEHOLDER_URL,
} from '../../../constants/marketing';
import CampaignSegmentCreator from '../CampaignSegmentCreator';
import CampaignSegmentReview from '../CampaignSegmentReview';
import useGetSegments from '../../../hooks/marketing/useGetSegments';
import StepFlowImage from '../../global/StepsFlow/StepFlowImage';
import CampaignTemplateDesigner from '../CampaignTemplateDesigner';
import useGetEmailTemplates from '../../../hooks/marketing/useGetEmailTemplates';
import {
  getSegmentsDescription,
  getSendAtText,
  getEmailTemplatePreviewUrl,
  getSmsTemplatePreviewUrl,
  transformSegment,
  isEmailTemplateValid,
  isSmsTemplateValid,
  getTemplateEstimatedSegments,
} from '../../../helpers/marketing';
import useGetSmsTemplates from '../../../hooks/marketing/useGetSmsTemplates';
import useUpsertCampaign from '../../../hooks/marketing/useUpsertCampaign';
import useLoadingModal from '../../../hooks/global/useLoadingModal';
import {
  formatDateForDateTimeLocal,
  formatDollarAmount,
  isNil,
  pluralize,
} from '../../../utils/global/helper';
import { Optional } from '../../../types/global';
import useGetCombinedSegmentCustomers from '../../../hooks/marketing/useGetCombinedSegmentCustomers';
import useChangedEffect from '../../../hooks/useChangedEffect';
import useGetMarketingData from '../../../hooks/marketing/useGetMarketingData';
import {
  getCampaignCost,
  invalidateTemplates,
} from '../../../services/marketing';
import useFinalizeCampaign from '../../../hooks/marketing/useFinalizeCampaign';
import useConfirm from '../../../hooks/useConfirm';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaign?: Optional<MarketingCampaignWithSource, 'id'> | null;
}

type SetValues = Dispatch<SetStateAction<CreateUpdateCampaignPayload>>;

function CampaignEditorModal({
  campaign: initialCampaign,
  isOpen,
  onClose,
}: Props) {
  const confirm = useConfirm();

  const [updateCampaign, setUpdateCampaign] = React.useState(
    initialCampaign && 'id' in initialCampaign && initialCampaign.id
      ? (initialCampaign as MarketingCampaignWithSource)
      : null,
  );

  const { data: marketingData, refetch } = useGetMarketingData();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { data: segments } = useGetSegments();
  const { data: { templates: emailTemplates = [] } = {} } =
    useGetEmailTemplates({
      enabled: !!updateCampaign,
    });
  const { data: { templates: smsTemplates = [] } = {} } = useGetSmsTemplates({
    enabled: !!updateCampaign,
  });

  const modalName = updateCampaign?.name ?? 'New Campaign';

  const refreshAfterUpdate = useCallback(async () => {
    await refetch();
    await invalidateTemplates();
  }, [refetch]);

  const { mutateAsync: upsertCampaign, isPending: isUpserting } =
    useUpsertCampaign(updateCampaign, {
      onError: msg => {
        toast.error(msg);
      },
      onMutate: ({ draft }) => {
        const isUpdate = !!updateCampaign;
        openLoadingModal({
          header: draft ? 'Saving Draft Campaign' : 'Saving Campaign',
          message: `Please wait while the campaign is being ${
            isUpdate ? 'updated' : 'created'
          }`,
        });
      },
    });

  const { mutateAsync: finalizeCampaign, isPending: isFinalizing } =
    useFinalizeCampaign({
      onError: msg => {
        toast.error(msg);
      },
      onMutate: ({ campaign }) => {
        const isScheduled = campaign.sendType === 'SCHEDULED';
        openLoadingModal({
          header: isScheduled ? 'Scheduling Campaign' : 'Starting Campaign',
          message: `Please wait while the campaign is being ${
            isScheduled ? 'scheduled' : 'started'
          }`,
        });
      },
    });

  const [isSegmentCreatorOpen, setIsSegmentCreatorOpen] = React.useState(false);
  const [templateEditorType, setTemplateEditorType] =
    React.useState<CampaignMedium | null>(null);
  const [initialSegment, setInitialSegment] =
    React.useState<CampaignSegment | null>(null);
  const [reviewSegment, setReviewSegment] =
    React.useState<CampaignSegment | null>(null);
  const [currentValues, setCurrentValues] =
    React.useState<CreateUpdateCampaignPayload | null>(null);

  const valuesRef = React.useRef<CreateUpdateCampaignPayload | null>(null);
  const setValuesRef = React.useRef<SetValues | null>(null);
  const submitRef = React.useRef<() => void>(() => {});
  const validateRef = React.useRef<() => void>(() => {});

  useChangedEffect(validateRef.current, [emailTemplates, smsTemplates]);

  const { data: segmentsData } = useGetCombinedSegmentCustomers({
    includeSegment: currentValues?.includeSegment as CampaignSegment | null,
    excludeSegment: currentValues?.excludeSegment as CampaignSegment | null,
    enabled: isOpen && !!currentValues,
  });
  const { include: prebuiltIncludeSegments, exclude: prebuiltExcludeSegments } =
    getPrebuiltSegments();

  const onSelectExistingSegment = (
    segment: MarketingCampaignSegmentWithFilters,
    setValues?: SetValues,
  ) => {
    const setValuesFunc = setValues ?? setValuesRef.current;
    if (!setValuesFunc) return;

    setValuesFunc(prev => ({
      ...prev,
      includeSegment: transformSegment(segment),
    }));
  };

  const getSelectedEmailTemplate = useCallback(
    (values: CreateUpdateCampaignPayload) => {
      const selectedEmailTemplate =
        values.medium === 'EMAIL' &&
        'content' in values &&
        'emailTemplateId' in values.content
          ? emailTemplates?.find(
              template =>
                template.id ===
                (values.content as EmailContentSchema).emailTemplateId,
            )
          : null;
      return selectedEmailTemplate;
    },
    [emailTemplates],
  );

  const getSelectedSmsTemplate = useCallback(
    (values: CreateUpdateCampaignPayload) => {
      const selectedSmsTemplate =
        values.medium === 'SMS' &&
        'content' in values &&
        'smsTemplateId' in values.content
          ? smsTemplates?.find(
              template =>
                template.id ===
                (values.content as SmsContentSchema).smsTemplateId,
            )
          : null;
      return selectedSmsTemplate;
    },
    [smsTemplates],
  );

  const finalValidationSchema = getUpsertCampaignSchema(
    emailTemplates,
    smsTemplates,
  );

  const onSubmit = useCallback(
    async (values: CreateUpdateCampaignPayload, draft: boolean) => {
      try {
        if (!marketingData?.id) return;

        const result = await upsertCampaign({
          body: values,
          draft,
        });
        if (!result || 'error' in result) return;

        if (draft) {
          await refreshAfterUpdate();
          onClose();
          return;
        }

        setUpdateCampaign(result.campaign);

        const {
          requirePayment,
          creditsRemaining,
          estimatedCredits,
          estimatedCampaignCost,
        } = await getCampaignCost({
          marketingBusinessId: marketingData.id,
          marketingCampaignId: result.campaign.id,
        });

        const isScheduled = result.campaign.sendType === 'SCHEDULED';
        const mediumText = values.medium === 'EMAIL' ? 'email' : 'SMS';
        const creditsCost = estimatedCredits[values.medium];

        const confirmed = await confirm({
          title: requirePayment
            ? 'Insufficient Credits'
            : `${isScheduled ? 'Schedule' : 'Start'} Campaign`,
          message: requirePayment
            ? `This campaign will use ${creditsCost} ${mediumText} credits. You currently have ${creditsRemaining} remaining credits on your account. If you send this campaign, we'll bill your account based on the email and SMS usage which is an estimated ${formatDollarAmount(
                estimatedCampaignCost / 100,
                true,
              )}.`
            : `This campaign will use ${creditsCost} of your ${creditsRemaining} remaining ${mediumText} credits.`,
          positiveText: 'Continue',
          negativeText: 'Cancel',
          dismissible: true,
        });

        if (!confirmed) {
          refreshAfterUpdate();
          return;
        }

        await finalizeCampaign({
          campaign: result.campaign,
        });

        await refreshAfterUpdate();
        onClose();
      } catch {
        // Nothing
      } finally {
        closeLoadingModal();
      }
    },
    [
      marketingData?.id,
      upsertCampaign,
      confirm,
      finalizeCampaign,
      refreshAfterUpdate,
      onClose,
      closeLoadingModal,
    ],
  );

  const onSubmitDraft = useCallback(async () => {
    try {
      await onSubmit(valuesRef.current as CreateUpdateCampaignPayload, true);
    } catch {
      // Nothing
    }
  }, [onSubmit]);

  const form = useMemo<StepFormData<CreateUpdateCampaignPayload>>(() => {
    const campaignMedium = initialCampaign?.mediums?.[0];
    const campaignTemplate = initialCampaign?.templates?.[0];
    const includeSegment =
      initialCampaign && segments?.length
        ? initialCampaign.includeSegmentId
          ? transformSegment(
              segments.find(
                segment => segment.id === initialCampaign.includeSegmentId,
              ),
            )
          : initialCampaign.prebuiltIncludeSegmentId
          ? prebuiltIncludeSegments[
              initialCampaign.prebuiltIncludeSegmentId as keyof typeof prebuiltIncludeSegments
            ]
          : prebuiltIncludeSegments.ALL_CUSTOMERS
        : prebuiltIncludeSegments.ALL_CUSTOMERS;
    const excludeSegment =
      initialCampaign && segments?.length
        ? initialCampaign.excludeSegmentId
          ? transformSegment(
              segments.find(
                segment => segment.id === initialCampaign.excludeSegmentId,
              ),
            )
          : initialCampaign.prebuiltExcludeSegmentId
          ? prebuiltExcludeSegments[
              initialCampaign.prebuiltExcludeSegmentId as keyof typeof prebuiltExcludeSegments
            ]
          : prebuiltExcludeSegments.NO_CUSTOMERS
        : prebuiltExcludeSegments.NO_CUSTOMERS;
    const initialContent =
      initialCampaign &&
      campaignTemplate &&
      campaignTemplate?.type === campaignMedium
        ? {
            [campaignMedium === 'SMS' ? 'smsTemplateId' : 'emailTemplateId']:
              campaignTemplate.id,
          }
        : {};

    const sendTime = initialCampaign?.sendType
      ? {
          type: initialCampaign.sendType,
          dateTime:
            initialCampaign.sendAt && !isPast(new Date(initialCampaign.sendAt))
              ? formatDateForDateTimeLocal(new Date(initialCampaign.sendAt))
              : '',
        }
      : {};

    return {
      initialValues: {
        name: initialCampaign?.name || '',
        medium: campaignMedium ?? 'SMS',
        includeSegment,
        excludeSegment,
        content: initialContent,
        sendTime,
        emailAddressId: initialCampaign?.source.email_address || '',
      },
      validationSchema: finalValidationSchema,
      onSubmit: async values => {
        await onSubmit(values, false);
      },
      steps: (values, { setValues }) => {
        const mediumText = values.medium === 'SMS' ? 'SMS' : 'email';
        const campaignText =
          values.medium === 'SMS' ? 'SMS campaign' : 'Email campaign';

        const selectedEmailTemplate = getSelectedEmailTemplate(values);
        const selectedSmsTemplate = getSelectedSmsTemplate(values);
        const selectedTemplateSource =
          selectedSmsTemplate?.source || selectedEmailTemplate?.source;

        const estimatedSegments = selectedTemplateSource
          ? getTemplateEstimatedSegments(
              segmentsData || 0,
              selectedTemplateSource,
            )
          : segmentsData || 0;

        const steps = [
          {
            title: 'Campaign overview',
            description: values.name
              ? `${values.name} - ${campaignText}`
              : `Enter campaign details`,
            selectButtonText: 'Update Overview',
            buttonText: 'Continue',
            isDefaultVisited:
              !!initialCampaign?.name && !!initialCampaign?.mediums?.length,
            inputs: [
              {
                name: 'name',
                contentType: 'text',
                label: 'Internal Name',
              },
              {
                contentType: 'button-select',
                name: 'medium',
                options: [
                  {
                    icon: SmsIcon,
                    title: 'SMS',
                    value: 'SMS',
                    description: 'Send this campaign via SMS',
                  },
                  {
                    icon: EmailIcon,
                    title: 'Email',
                    value: 'EMAIL',
                    description: 'Send this campaign via email',
                  },
                ],
              },
            ],
          },
          {
            title: 'To',
            description: `Who are you sending this ${mediumText} to?`,
            visitedDescription: (
              <>
                {getSegmentsDescription(
                  values.includeSegment,
                  values.excludeSegment,
                )}
                <Typography
                  variant="inherit"
                  fontWeight="bold"
                  component="span">
                  {!isNil(segmentsData)
                    ? ` ${segmentsData} ${pluralize(
                        'customer',
                        segmentsData || 0,
                      )}`
                    : ''}
                  {estimatedSegments
                    ? `${
                        segmentsData ? '.' : ''
                      }\nCampaign is estimated to use ${estimatedSegments} ${mediumText} ${pluralize(
                        'credit',
                        estimatedSegments,
                      )}.`
                    : ''}
                </Typography>
              </>
            ),
            selectButtonText: 'Add Recipients',
            buttonText: 'Continue',
            isDefaultVisited:
              (!!initialCampaign?.includeSegmentId ||
                (!!initialCampaign?.prebuiltIncludeSegmentId &&
                  initialCampaign.prebuiltIncludeSegmentId !==
                    'ALL_CUSTOMERS')) &&
              (!!initialCampaign?.excludeSegmentId ||
                !!initialCampaign?.prebuiltExcludeSegmentId ||
                initialCampaign.prebuiltExcludeSegmentId !== 'NO_CUSTOMERS'),
            inputs: [
              {
                contentType: 'category-select',
                name: 'includeSegment.id',
                placeholder: 'Select recipients',
                label: 'Send to',
                options: [
                  ...Object.entries(prebuiltIncludeSegments).map(
                    ([id, segment]) => {
                      const content =
                        PREBUILT_INCLUDE_SEGMENTS_CONTENT[
                          id as keyof typeof PREBUILT_INCLUDE_SEGMENTS_CONTENT
                        ];
                      return {
                        category: 'Pre-Built Segments',
                        value: id,
                        ...content,
                        onClick: () => {
                          setValues(prev => ({
                            ...prev,
                            includeSegment: segment,
                          }));
                        },
                      };
                    },
                  ),
                  {
                    category: 'Your Segments',
                    value: 'CUSTOM_SEGMENT',
                    label: 'Build a custom segment',
                    icon: AddIcon,
                    onClick: () => {
                      setIsSegmentCreatorOpen(true);
                    },
                  },
                  ...(segments?.map(segment => ({
                    category: 'Your Segments',
                    value: segment.id as unknown as string,
                    label: segment.name,
                    onClick: () => {
                      onSelectExistingSegment(segment, setValues);
                    },
                  })) ?? []),
                ],
              },
              {
                contentType: 'category-select',
                name: 'excludeSegment.id',
                placeholder: 'Select recipients',
                label: 'Exclude customers (optional)',
                options: [
                  ...Object.entries(prebuiltExcludeSegments).map(
                    ([id, segment]) => {
                      const content =
                        PREBUILT_EXCLUDE_SEGMENTS_CONTENT[
                          id as keyof typeof PREBUILT_EXCLUDE_SEGMENTS_CONTENT
                        ];
                      return {
                        category:
                          id === 'NO_CUSTOMERS' ? '' : 'Pre-Built Segments',
                        value: id,
                        ...content,
                        onClick: () => {
                          setValues(prev => ({
                            ...prev,
                            excludeSegment: segment,
                          }));
                        },
                      };
                    },
                  ),
                ],
              },
            ],
          },
          values.medium === 'EMAIL' &&
            marketingData?.emailAddresses &&
            marketingData.emailAddresses.length > 0 && {
              title: 'Send from',
              description: values.emailAddressId
                ? marketingData.emailAddresses.find(
                    email => email.id === values.emailAddressId,
                  )?.email_address
                : 'Choose the sending email',
              selectButtonText: 'Update Email',
              buttonText: 'Continue',
              isDefaultVisited:
                !!initialCampaign?.name && !!initialCampaign?.mediums?.length,
              inputs: [
                {
                  contentType: 'select',
                  name: 'emailAddressId',
                  label: 'From Email Address',
                  required: true,
                  options: marketingData.emailAddresses.map(email => ({
                    value: email.id,
                    label: `${email.display_name} (${email.email_address})`,
                  })),
                },
              ],
            },
          {
            title: 'Send time',
            description:
              values.sendTime &&
              'type' in values.sendTime &&
              values.sendTime.type
                ? getSendAtText(values.sendTime)
                : `When should we send this ${mediumText}?`,
            selectButtonText: `${values.sendTime ? 'Update' : 'Add'} Send Time`,
            buttonText: 'Continue',
            isDefaultVisited:
              sendTime && (sendTime.type === 'NOW' || !!sendTime.dateTime),
            inputs: [
              {
                contentType: 'button-select',
                name: 'sendTime.type',
                horizontal: true,
                options: [
                  {
                    title: 'Send now',
                    description: `Send your ${mediumText} now`,
                    value: 'NOW',
                    icon: Send,
                  },
                  {
                    title: 'Schedule a time',
                    description: 'Optimize your sending time',
                    value: 'SCHEDULED',
                    icon: ScheduleSend,
                  },
                ],
              },
              values.sendTime &&
                'type' in values.sendTime &&
                values.sendTime.type === 'SCHEDULED' && {
                  contentType: 'text',
                  type: 'datetime-local',
                  name: 'sendTime.dateTime',
                  label: 'Send Time',
                },
            ].filter(Boolean) as StepInput<typeof values>[],
          },
          {
            title: 'Content',
            description: selectedEmailTemplate
              ? `Selected template: ${
                  selectedEmailTemplate?.source?.name || 'Unnamed Template'
                }\nSubject: ${selectedEmailTemplate?.source?.subject || ''}`
              : selectedSmsTemplate
              ? `Selected template: ${
                  selectedSmsTemplate?.source?.name || 'Unnamed Template'
                }`
              : `Design the content for your ${mediumText}`,
            selectButtonText: 'Design Content',
            isDefaultVisited: !!initialCampaign && !!initialContent,
            inputs: [
              {
                contentType: 'invisible',
                name: `content.${
                  values.medium === 'EMAIL'
                    ? 'emailTemplateId'
                    : 'smsTemplateId'
                }`,
              },
            ].filter(Boolean) as any[],
            onNavigate: () => {
              if (!values.medium) return;
              setTemplateEditorType(values.medium);
            },
            isError: selectedEmailTemplate
              ? !isEmailTemplateValid(selectedEmailTemplate.source)
              : selectedSmsTemplate
              ? !isSmsTemplateValid(selectedSmsTemplate.source)
              : false,
          },
        ].filter(Boolean) as StepFormData<CreateUpdateCampaignPayload>['steps'];
        return steps;
      },
    } as StepFormData<CreateUpdateCampaignPayload>;
  }, [
    initialCampaign,
    segments,
    prebuiltIncludeSegments,
    prebuiltExcludeSegments,
    finalValidationSchema,
    onSubmit,
    getSelectedEmailTemplate,
    getSelectedSmsTemplate,
    segmentsData,
    marketingData?.emailAddresses,
  ]);

  const rightContent = useCallback(
    (values: CreateUpdateCampaignPayload) => {
      const selectedEmailTemplate = getSelectedEmailTemplate(values);
      const selectedSmsTemplate = getSelectedSmsTemplate(values);
      const previewUrl = selectedEmailTemplate
        ? getEmailTemplatePreviewUrl(selectedEmailTemplate)
        : selectedSmsTemplate
        ? getSmsTemplatePreviewUrl(selectedSmsTemplate)
        : null;
      return (
        <StepFlowImage
          src={previewUrl || TEMPLATE_PLACEHOLDER_URL}
          fallbackSrc={TEMPLATE_PLACEHOLDER_URL}
          keepPrevious
          isFullWidth={!previewUrl || values.medium === 'EMAIL'}
          imageStyle={{
            maxWidth: '80%',
            margin: '0 auto',
          }}
        />
      );
    },
    [getSelectedEmailTemplate, getSelectedSmsTemplate],
  );

  return (
    <>
      <FullPageModal
        modalOpen={isOpen}
        name={modalName}
        setModalOpen={onClose}
        large>
        <StepsFlow
          form={form}
          isSequential={false}
          isFirstStepRequired
          defaultIsFirstStepComplete={
            !!initialCampaign?.name && !!initialCampaign?.mediums?.length
          }
          rightContent={rightContent}
          setValuesRef={setValuesRef}
          valuesRef={valuesRef}
          submitRef={submitRef}
          validateRef={validateRef}
          submitButton={{
            text: 'Create',
          }}
          secondaryButton={{
            text: 'Finish Later',
            onClick: onSubmitDraft,
            disabled: isUpserting || isFinalizing,
          }}
          onValuesChange={setCurrentValues}
        />
      </FullPageModal>
      {isSegmentCreatorOpen && (
        <CampaignSegmentCreator
          segment={initialSegment}
          isOpen={isSegmentCreatorOpen}
          onClose={() => {
            setInitialSegment(null);
            setIsSegmentCreatorOpen(false);
          }}
          onReview={setReviewSegment}
        />
      )}
      {reviewSegment && (
        <CampaignSegmentReview
          segment={reviewSegment}
          onClose={() => {
            setInitialSegment(reviewSegment);
            setReviewSegment(null);
            setIsSegmentCreatorOpen(true);
          }}
          onCreateSuccess={segment => {
            setInitialSegment(null);
            setReviewSegment(null);
            setIsSegmentCreatorOpen(false);
            onSelectExistingSegment(segment);
          }}
          backButton
        />
      )}
      {templateEditorType && (
        <CampaignTemplateDesigner
          medium={templateEditorType}
          isOpen={!!templateEditorType}
          onClose={() => setTemplateEditorType(null)}
          selectedTemplate={
            currentValues
              ? templateEditorType === 'SMS'
                ? getSelectedSmsTemplate(currentValues)
                : getSelectedEmailTemplate(currentValues)
              : null
          }
          onSelectTemplate={template => {
            setValuesRef.current?.(prev => {
              const newValue =
                templateEditorType === 'SMS'
                  ? ({
                      smsTemplateId: template?.id,
                    } as SmsContentSchema)
                  : ({
                      emailTemplateId: template?.id,
                    } as EmailContentSchema);
              return {
                ...prev,
                content: newValue,
              };
            });
          }}
        />
      )}
    </>
  );
}

export default CampaignEditorModal;
